import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { getFirstMiddleName } from "@gyandiksha/shared-ui/utils";
import {
  Avatar, DropdownWrapper, Iconify,
  MASKEDTYPE, MaskedValue
} from "@repo/design-system/components/ui";
import { useTheme } from "../../contexts/index.jsx";

import { getAuthProps, getPortalSelectionAppUrl, signOut } from "@/services";
import { useGetProfilePhotoQuery } from "../../app/services/CommonAPI";

import "./UserProfileMenu.scss";

/*
  User avatar dropdown (photo, name, masked email, Profile / Settings, Logout).
  Single source for every header that shows the logged-in user — change it here and it reflects everywhere.
  `menuItems` overrides the default Profile / Settings items (e.g. apps that don't have those routes).
*/
const UserProfileMenu = ({ menuItems }) => {

  const { theme } = useTheme();
  const navigate = useNavigate();
  const userData = getAuthProps();
  const isCompanyUser = Boolean(userData?.companyId);
  const displayName = getFirstMiddleName(userData?.fullName, userData?.firstName, userData?.lastName)
    || userData?.name || userData?.personName;
  const logoutUrl = isCompanyUser
    ? getPortalSelectionAppUrl("placementManagement", "/login")
    : undefined;

  const { data: profilePhotoData } = useGetProfilePhotoQuery(undefined, {
    skip: !userData || isCompanyUser || Boolean(userData?.isErpLoggedIn),
  });

  const profileImageUrl = profilePhotoData?.data?.url
    || userData?.profilePhoto
    || userData?.avatarUrl
    || userData?.photoUrl
    || userData?.photo
    || undefined;

  const defaultMenuItems = [
    {
      icon: "lucide:user",
      label: "Profile",
      onClick: () => navigate("/profile"),
    },
    // Company users have no Settings page (no 2FA/account settings of their own) — student/staff portals do.
    ...(isCompanyUser ? [] : [{
      icon: "lucide:settings",
      label: "Settings",
      onClick: () => navigate("/settings"),
    }]),
  ];

  const userMenuItems = menuItems ?? defaultMenuItems;

  return (
    <DropdownWrapper toggleElement={
      <div className="user-menu">
        <Avatar src={profileImageUrl} alt={displayName} size="small"
          shape="circle" tooltip={false} />
        <Iconify icon="lucide:chevron-down" width={14} />
      </div>}>
      <div className={`user-dropdown ${theme}`}>
        <div className="user-info">
          <Avatar src={profileImageUrl} alt={displayName} size="medium" shape="circle" />
          <div>
            <div className="user-name">{displayName}</div>
            <div className="user-email">
              <MaskedValue value={userData?.email}
                maskedType={MASKEDTYPE.EMAIL}
                isIconVisible={false} />
            </div>
          </div>
        </div>

        <div className="dropdown-divider"></div>
        {!userData?.isErpLoggedIn &&
          <>
            <div className="user-menu-content">
              {userMenuItems.map((item, index) => (
                <div key={index} className="user-menu-item" onClick={item.onClick}>
                  <Iconify icon={item.icon} width={16} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="dropdown-divider"></div>

            <button className="logout-btn" onClick={() => signOut(logoutUrl)}>
              <Iconify icon="lucide:log-out" width={16} />
              <span>Logout</span>
            </button>
          </>
        }
      </div>
    </DropdownWrapper>
  );
};

UserProfileMenu.propTypes = {
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.string,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })),
};

export default UserProfileMenu;
